/**
 * Vue Customizer Manager
 * Manages Vue app lifecycle, state preservation, and section settings bridge
 * for Shopify Sections API integration
 */

(function () {
  'use strict';

  // Registry to track all customizer instances by section ID
  window.VueCustomizerRegistry = window.VueCustomizerRegistry || new Map();

  /**
   * VueCustomizerManager class
   * Handles Vue app lifecycle management for theme editor compatibility
   */
  class VueCustomizerManager {
    /**
     * @param {string} sectionId - The Shopify section ID
     */
    constructor(sectionId) {
      this.sectionId = sectionId;
      this.app = null;
      this.container = null;
      this.config = null;
      this.data = null;
      this.storageKey = `vue-customizer-state-${sectionId}`;
      this.isDestroyed = false;
    }

    /**
     * Initialize the Vue customizer
     * @param {HTMLElement} container - The DOM element to mount Vue app
     * @param {Object} config - Section settings from Shopify
     * @param {Object} data - Product/customizer data
     * @returns {Object} The Vue app instance
     */
    init(container, config, data) {
      if (this.isDestroyed) {
        console.warn(`[VueCustomizerManager] Cannot init destroyed manager for section ${this.sectionId}`);
        return null;
      }

      this.container = container;
      this.config = config || {};
      this.data = data;

      // Check for existing state to restore
      const savedState = this.loadState();

      // Create Vue app using the global createVueCustomizerApp function
      if (typeof window.createVueCustomizerApp === 'function') {
        this.app = window.createVueCustomizerApp(this.sectionId, this.data, this.config);

        if (this.app && container) {
          this.app.mount(container);

          // Restore state after mount if available
          if (savedState) {
            this.$nextTick(() => {
              this.restoreState(savedState);
              this.hydrateFromPreviewTexts();
            });
          } else {
            this.$nextTick(() => {
              this.hydrateFromPreviewTexts();
            });
          }

          // Register this manager in the global registry
          window.VueCustomizerRegistry.set(this.sectionId, this);

          console.log(`[VueCustomizerManager] Initialized for section ${this.sectionId}`);
        }
      } else {
        console.error('[VueCustomizerManager] createVueCustomizerApp function not found');
      }

      return this.app;
    }

    /**
     * Helper to run callback after Vue's next tick
     * @param {Function} callback
     */
    $nextTick(callback) {
      if (this.app && this.app._instance && this.app._instance.proxy) {
        this.app._instance.proxy.$nextTick(callback);
      } else {
        // Fallback to setTimeout
        setTimeout(callback, 0);
      }
    }

    /**
     * Get the Vue component instance proxy
     * @returns {Object|null}
     */
    getProxy() {
      if (this.app && this.app._instance) {
        return this.app._instance.proxy;
      }
      return null;
    }

    /**
     * Save current Vue state to sessionStorage
     * Serializes texts, selection handles, photo data, packs, and quantity
     */
    saveState() {
      const proxy = this.getProxy();
      if (!proxy) {
        console.warn(`[VueCustomizerManager] No proxy available to save state for section ${this.sectionId}`);
        return;
      }

      try {
        const state = {
          texts: { ...proxy.texts },
          selection: {
            font: proxy.selection.font ? { handle: proxy.selection.font.handle } : null,
            color: proxy.selection.color ? { handle: proxy.selection.color.handle } : null,
            color_alt: proxy.selection.color_alt ? { handle: proxy.selection.color_alt.handle } : null,
            set: proxy.selection.set ? { handle: proxy.selection.set.handle } : null,
          },
          photo: proxy.photo
            ? {
                upload: proxy.photo.upload,
                x: proxy.photo.x,
                y: proxy.photo.y,
                z: proxy.photo.z,
              }
            : null,
          packs: proxy.packs,
          quantity: proxy.quantity,
          isConfirmed: proxy.isConfirmed,
          timestamp: Date.now(),
        };

        sessionStorage.setItem(this.storageKey, JSON.stringify(state));
        console.log(`[VueCustomizerManager] State saved for section ${this.sectionId}`);
      } catch (e) {
        console.error(`[VueCustomizerManager] Failed to save state for section ${this.sectionId}:`, e);
      }
    }

    /**
     * Load state from sessionStorage
     * @returns {Object|null} The saved state or null
     */
    loadState() {
      try {
        const stored = sessionStorage.getItem(this.storageKey);
        if (stored) {
          const state = JSON.parse(stored);

          // Check if state is too old (30 minutes)
          const maxAge = 30 * 60 * 1000;
          if (state.timestamp && Date.now() - state.timestamp > maxAge) {
            sessionStorage.removeItem(this.storageKey);
            console.log(`[VueCustomizerManager] Expired state removed for section ${this.sectionId}`);
            return null;
          }

          return state;
        }
      } catch (e) {
        console.error(`[VueCustomizerManager] Failed to load state for section ${this.sectionId}:`, e);
      }
      return null;
    }

    /**
     * Restore saved state to Vue instance
     * Matches saved handles against available options
     * @param {Object} state - The state object to restore
     */
    restoreState(state) {
      const proxy = this.getProxy();
      if (!proxy || !state) return;

      try {
        // Restore texts
        if (state.texts) {
          Object.keys(state.texts).forEach((key) => {
            if (typeof proxy.texts[key] !== 'undefined') {
              proxy.texts[key] = state.texts[key];
            }
          });
        }

        // Restore selections by matching handles
        if (state.selection) {
          // Restore font
          if (state.selection.font && state.selection.font.handle && proxy.options.fonts) {
            const font = proxy.options.fonts.find((f) => f.handle === state.selection.font.handle);
            if (font) proxy.selection.font = font;
          }

          // Restore color
          if (state.selection.color && state.selection.color.handle && proxy.options.colors) {
            const color = proxy.options.colors.find((c) => c.handle === state.selection.color.handle);
            if (color) proxy.selection.color = color;
          }

          // Restore color_alt
          if (state.selection.color_alt && state.selection.color_alt.handle && proxy.options.color_alts) {
            const colorAlt = proxy.options.color_alts.find((c) => c.handle === state.selection.color_alt.handle);
            if (colorAlt) proxy.selection.color_alt = colorAlt;
          }

          // Restore set
          if (state.selection.set && state.selection.set.handle && proxy.options.sets) {
            const set = proxy.options.sets.find((s) => s.handle === state.selection.set.handle);
            if (set) proxy.selection.set = set;
          }
        }

        // Restore photo
        if (state.photo && proxy.photo) {
          proxy.photo.upload = state.photo.upload || null;
          proxy.photo.x = state.photo.x || 0;
          proxy.photo.y = state.photo.y || 0;
          proxy.photo.z = state.photo.z || 0;
        }

        // Restore packs and quantity
        if (typeof state.packs !== 'undefined') {
          proxy.packs = state.packs;
        }
        if (typeof state.quantity !== 'undefined') {
          proxy.quantity = state.quantity;
        }

        // Restore confirmation state only if customization is complete
        if (state.isConfirmed && proxy.isCustomizationComplete) {
          proxy.isConfirmed = true;
        }

        // Trigger canvas update
        if (typeof proxy.updateCustomizer === 'function') {
          proxy.updateCustomizer();
        }

        console.log(`[VueCustomizerManager] State restored for section ${this.sectionId}`);
      } catch (e) {
        console.error(`[VueCustomizerManager] Failed to restore state for section ${this.sectionId}:`, e);
      }
    }

    /**
     * Clear saved state from sessionStorage
     */
    clearState() {
      try {
        sessionStorage.removeItem(this.storageKey);
        console.log(`[VueCustomizerManager] State cleared for section ${this.sectionId}`);
      } catch (e) {
        console.error(`[VueCustomizerManager] Failed to clear state for section ${this.sectionId}:`, e);
      }
    }

    /**
     * Hydrate Vue texts from the personalized-grid localStorage key so names
     * typed into the grid preview inputs carry into the full customizer.
     * Only fills empty slots — never clobbers existing text. Line_2 is only
     * hydrated when the label actually supports a second line (skipped for
     * one-line labels like mini / mini TagPals via the app's isTextLine).
     */
    hydrateFromPreviewTexts() {
      const proxy = this.getProxy();
      if (!proxy || !proxy.texts) return;
      try {
        const raw = localStorage.getItem('personalized-grid:preview-texts');
        if (!raw) return;
        const parsed = JSON.parse(raw);
        let changed = false;
        if (typeof parsed.Line_1 === 'string' && parsed.Line_1 && typeof proxy.texts.Line_1 !== 'undefined' && !proxy.texts.Line_1) {
          proxy.texts.Line_1 = parsed.Line_1;
          changed = true;
        }
        const supportsLine2 = typeof proxy.isTextLine !== 'function' || proxy.isTextLine('Line_2');
        if (
          supportsLine2 &&
          typeof parsed.Line_2 === 'string' &&
          parsed.Line_2 &&
          typeof proxy.texts.Line_2 !== 'undefined' &&
          !proxy.texts.Line_2
        ) {
          proxy.texts.Line_2 = parsed.Line_2;
          changed = true;
        }
        if (changed && typeof proxy.updateCustomizer === 'function') {
          proxy.updateCustomizer();
        }
      } catch (e) {
        console.error(`[VueCustomizerManager] Failed to hydrate preview texts for section ${this.sectionId}:`, e);
      }
    }

    /**
     * Apply section settings from Shopify theme editor
     * Updates Vue reactively when settings change
     * @param {Object} settings - Section settings object
     */
    applySettings(settings) {
      const proxy = this.getProxy();
      if (!proxy || !settings) return;

      this.config = settings;

      try {
        // Apply layout setting
        if (settings.layout) {
          const wrapper = this.container?.closest('.vue-customizer-wrapper');
          if (wrapper) {
            wrapper.dataset.layout = settings.layout;
          }
        }

        // Apply canvas width setting
        if (settings.canvas_width) {
          const canvasWrapper = this.container?.closest('.vue-customizer-wrapper')?.querySelector('.vue-canvas-wrapper');
          if (canvasWrapper) {
            canvasWrapper.style.maxWidth = `${settings.canvas_width}px`;
          }
        }

        // Apply visibility settings
        // These are read by the template via CSS classes or v-if conditions
        // The Vue app can check these via window.vueCustomizerSettings[sectionId]
        window.vueCustomizerSettings = window.vueCustomizerSettings || {};
        window.vueCustomizerSettings[this.sectionId] = {
          showFontSelector: settings.show_font_selector !== false,
          showColorSelector: settings.show_color_selector !== false,
          showIconSelector: settings.show_icon_selector !== false,
          previewTextLine1: settings.preview_text_line1 || '',
        };

        // If preview text is set, update Line_1
        if (settings.preview_text_line1 && proxy.texts && !proxy.texts.Line_1) {
          proxy.texts.Line_1 = settings.preview_text_line1;
        }

        console.log(`[VueCustomizerManager] Settings applied for section ${this.sectionId}`);
      } catch (e) {
        console.error(`[VueCustomizerManager] Failed to apply settings for section ${this.sectionId}:`, e);
      }
    }

    /**
     * Destroy the Vue app and cleanup
     */
    destroy() {
      // Save state before destroying
      this.saveState();

      if (this.app) {
        try {
          this.app.unmount();
          console.log(`[VueCustomizerManager] App unmounted for section ${this.sectionId}`);
        } catch (e) {
          console.error(`[VueCustomizerManager] Error unmounting app for section ${this.sectionId}:`, e);
        }
      }

      // Remove from registry
      window.VueCustomizerRegistry.delete(this.sectionId);

      // Mark as destroyed
      this.isDestroyed = true;
      this.app = null;
      this.container = null;

      console.log(`[VueCustomizerManager] Destroyed manager for section ${this.sectionId}`);
    }

    /**
     * Check if this manager is still active
     * @returns {boolean}
     */
    isActive() {
      return !this.isDestroyed && this.app !== null;
    }
  }

  // Export to global scope
  window.VueCustomizerManager = VueCustomizerManager;

  /**
   * Utility function to get or create a manager for a section
   * @param {string} sectionId
   * @returns {VueCustomizerManager}
   */
  window.getVueCustomizerManager = function (sectionId) {
    if (window.VueCustomizerRegistry.has(sectionId)) {
      const manager = window.VueCustomizerRegistry.get(sectionId);
      if (manager.isActive()) {
        return manager;
      }
    }
    return new VueCustomizerManager(sectionId);
  };

  /**
   * Utility function to destroy all managers
   * Useful for cleanup during navigation
   */
  window.destroyAllVueCustomizerManagers = function () {
    window.VueCustomizerRegistry.forEach((manager, sectionId) => {
      manager.destroy();
    });
    window.VueCustomizerRegistry.clear();
    console.log('[VueCustomizerManager] All managers destroyed');
  };
})();
